import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lookupFamily } from '../../lib/graphApi';
import type { FamilyLookupResult } from '../../lib/graphApi';
import { actorDetailPath, techniqueDetailPath } from '../../lib/paths';
import EntityDetailShell from '../entity/EntityDetailShell';
import EntityHeader, { type HeaderAction } from '../entity/EntityHeader';
import FactsBand, { type FactsStat } from '../entity/FactsBand';
import DocumentsTable, { type DocumentRow } from '../entity/DocumentsTable';
import CoOccurrenceTable, { type CoOccurrenceRow } from '../entity/CoOccurrenceTable';
import { sparklineFromDocs } from '../entity/sparklineFromDocs';
import { ENTITY_COLORS } from '../../lib/entityColors';

export default function FamilyDetailPage() {
  const params = useParams();
  const navigate = useNavigate();

  const familyName = params.familyName ? decodeURIComponent(params.familyName) : '';

  const { data, isLoading } = useQuery<FamilyLookupResult>({
    queryKey: ['family-detail', familyName],
    queryFn: () => lookupFamily(familyName),
    enabled: !!familyName,
  });

  const stats = data?.stats;
  const aliases = data?.aliases || [];
  const actors = data?.actors || [];
  const techniques = data?.techniques || [];
  const documents: DocumentRow[] = useMemo(() => (data?.documents || []).map((d) => ({ ...d })), [data?.documents]);
  const displayName = data?.common_name || familyName;
  const malpediaSearchUrl = `https://malpedia.caad.fkie.fraunhofer.de/details/${encodeURIComponent(familyName)}`;
  const graphUrl = `/graph?seed=${encodeURIComponent(displayName)}&label=MalwareFamily`;

  const coRows: CoOccurrenceRow[] = useMemo(() => {
    const rows: CoOccurrenceRow[] = [];
    for (const a of actors) {
      rows.push({
        id: `actor::${a.actor_id || a.common_name}`,
        type: 'Actor',
        value: a.common_name || a.actor_id,
        href: actorDetailPath(a.common_name || a.actor_id),
        count: 1,
      });
    }
    for (const t of techniques) {
      rows.push({
        id: `tech::${t.attack_id}`,
        type: 'Technique',
        value: `${t.attack_id}${t.name ? ` — ${t.name}` : ''}`,
        href: techniqueDetailPath(t.attack_id),
        count: 1,
      });
    }
    return rows;
  }, [actors, techniques]);

  if (isLoading) {
    return (
      <div className="ioc-detail-page">
        <button className="ioc-detail-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="ioc-detail-loading">Loading family</div>
      </div>
    );
  }

  const actions: HeaderAction[] = [];
  if (data?.malpedia_url) actions.push({ key: 'mal', label: 'Malpedia', href: data.malpedia_url, external: true });
  actions.push({ key: 'graph', label: 'Open in Graph', to: graphUrl });

  const factsStats: FactsStat[] = [];
  if (stats?.doc_count) factsStats.push({ label: 'reports', value: stats.doc_count });
  if (stats?.actor_count) factsStats.push({ label: 'actors', value: stats.actor_count });
  if (stats?.technique_count) factsStats.push({ label: 'techniques', value: stats.technique_count });

  const badges = data?.platform ? <span className="ioc-detail-meta-tag">{data.platform}</span> : null;

  const belowValue = aliases.length > 0 ? (
    <div className="actor-detail-aliases">
      <span className="actor-detail-aliases-label">Also known as:</span>
      {aliases.map((alias) => <span key={alias} className="actor-detail-alias-chip">{alias}</span>)}
    </div>
  ) : null;

  return (
    <EntityDetailShell
      header={
        <EntityHeader
          badge="Malware Family"
          badgeClassName="actor-detail-badge"
          value={displayName}
          copyValue={familyName}
          actions={actions}
          metaStrip={badges}
          belowValue={belowValue}
        />
      }
      facts={
        factsStats.length > 0 ? (
          <FactsBand
            stats={factsStats}
            sparkline={{
              firstSeen: documents.reduce<string | null>((m, d) => (d.date && (!m || d.date < m) ? d.date : m), null),
              lastSeen: documents.reduce<string | null>((m, d) => (d.date && (!m || d.date > m) ? d.date : m), null),
              series: sparklineFromDocs(documents),
            }}
            borderColor={ENTITY_COLORS.family}
          />
        ) : null
      }
      description={data?.description ? <p className="ioc-detail-description">{data.description}</p> : null}
      notFound={data && !data.found ? (
        <div className="ioc-detail-not-found">
          Family not indexed in knowledge graph.{' '}
          <a href={malpediaSearchUrl} target="_blank" rel="noreferrer">Search on Malpedia</a>
        </div>
      ) : null}
      documents={documents.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Referenced in {documents.length} document{documents.length !== 1 ? 's' : ''}</h3>
          <DocumentsTable documents={documents} />
        </>
      ) : null}
      cooccurrence={coRows.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Co-attributed entities</h3>
          <CoOccurrenceTable rows={coRows} typeFacetLabel="Type" />
        </>
      ) : null}
    />
  );
}
